// Pure product/catalog helpers shared across storefront components.
// Relocated from EcommerceWebsite.jsx during the storefront componentization pass.

/**
 * Group flat API products by base name, combining their variants.
 *
 * NOTE: this copies `imageColorMaps` onto the group (the original implementation
 * dropped this field during grouping, which silently broke the admin's
 * per-image color-mapping feature — swatches/image-sync always fell back to
 * the legacy `colors` array instead). See DPC admin-alignment audit.
 */
export const groupProductsByBaseName = (products) => {
  const groups = {};

  products.forEach((product) => {
    const baseName = product.name;

    if (!groups[baseName]) {
      groups[baseName] = {
        id: product.id,
        productId: product.id,
        baseName,
        name: baseName,
        category: product.category,
        categoryId: product.categoryId,
        brand: product.brand || 'DPC Collection',
        description: product.description,
        material: product.material,
        closure: product.closure,
        features: product.features || [],
        tags: product.tags || [],
        isFeatured: Boolean(product.isFeatured),
        colors: product.colors || [],
        color: product.color,
        colorCode: product.colorCode,
        images: product.images || [],
        imageColorMaps: product.imageColorMaps || [],
        variants: [],
      };
    }

    if (Array.isArray(product.variants) && product.variants.length > 0) {
      product.variants.forEach((variant, index) => {
        const variantColor = variant.color || variant.value || product.color || (product.colors?.[0]?.name ?? null);
        const variantColorCode = variant.colorCode || variant.code || product.colorCode || (product.colors?.[0]?.code ?? '#CCCCCC');
        const variantId = variant.variantId || variant.id || `${product.id}-db-${index}`;
        const variantPrice = typeof variant.price === 'number' ? variant.price : product.price;
        const variantStock = typeof variant.stockQuantity === 'number'
          ? variant.stockQuantity
          : (typeof variant.stock === 'number' ? variant.stock : product.stockQuantity);

        groups[baseName].variants.push({
          id: variantId,
          variantId,
          color: variantColor,
          colorCode: variantColorCode,
          attributes: variant.attributes || {},
          price: variantPrice,
          originalPrice: typeof variant.originalPrice === 'number' ? variant.originalPrice : product.originalPrice,
          image: variant.image || product.image,
          variantImages: Array.isArray(variant.images) && variant.images.length > 0 ? variant.images : (product.images || [product.image]),
          images: Array.isArray(variant.images) && variant.images.length > 0 ? variant.images : (product.images || [product.image]),
          inStock: typeof variant.inStock === 'boolean' ? variant.inStock : variantStock > 0,
          stockQuantity: variantStock,
          fastShipping: typeof variant.fastShipping === 'boolean' ? variant.fastShipping : product.fastShipping,
          sale: typeof variant.sale === 'boolean' ? variant.sale : Boolean((typeof variant.originalPrice === 'number' ? variant.originalPrice : product.originalPrice) > variantPrice),
          badge: variant.badge || product.badge,
          rating: typeof variant.rating === 'number' ? variant.rating : product.rating,
          reviews: typeof variant.reviews === 'number' ? variant.reviews : product.reviews,
        });
      });
    } else {
      groups[baseName].variants.push({
        id: product.id,
        variantId: product.id,
        color: product.color || (product.colors?.[0]?.name ?? null),
        colorCode: product.colorCode || (product.colors?.[0]?.code ?? '#CCCCCC'),
        size: typeof product.dimensions === 'string' ? product.dimensions : null,
        attributes: {},
        price: product.price,
        originalPrice: product.originalPrice,
        image: product.image,
        variantImages: product.images || [product.image],
        images: product.images || [product.image],
        inStock: product.inStock,
        stockQuantity: product.stockQuantity,
        fastShipping: product.fastShipping,
        sale: product.originalPrice > product.price,
        badge: product.badge,
        rating: product.rating,
        reviews: product.reviews,
      });
    }
  });

  return Object.values(groups);
};

/**
 * True stock/price/rating/etc. always comes from a real variant or the
 * product itself — never from a ProductImageColorMap row, which only ever
 * carries imageUrl/colorName/colorCode and has no stock fields at all. An
 * earlier version of this function returned that raw row as "the variant"
 * whenever one existed, which silently made every in-stock product with a
 * color-swatch image look out-of-stock (stockFields.inStock was undefined,
 * and callers treat that as false). Now the image-color-map only ever
 * overrides the cosmetic display fields on top of a real variant/product.
 */
export const getSelectedVariant = (productGroup) => {
  const baseVariant = (productGroup.variants && productGroup.variants.length > 0)
    ? productGroup.variants[0]
    : {
      id: productGroup.productId || productGroup.id,
      variantId: productGroup.productId || productGroup.id,
      name: productGroup.baseName || productGroup.name,
      color: productGroup.colors && productGroup.colors[0] ? productGroup.colors[0].name : 'Default',
      colorCode: productGroup.colors && productGroup.colors[0] ? productGroup.colors[0].code : '#000000',
      price: productGroup.price,
      originalPrice: productGroup.originalPrice,
      image: productGroup.image,
      images: productGroup.images || [productGroup.image],
      variantImages: productGroup.images || [productGroup.image],
      inStock: productGroup.inStock,
      stockQuantity: productGroup.stockQuantity,
      fastShipping: productGroup.fastShipping || false,
      sale: productGroup.originalPrice && productGroup.originalPrice > productGroup.price,
      badge: productGroup.badge || (productGroup.isFeatured ? 'Featured' : null),
      rating: productGroup.rating || 4.0,
      reviews: productGroup.reviews || 0,
      dimensions: productGroup.dimensions,
    };

  if (productGroup.imageColorMaps && productGroup.imageColorMaps.length > 0) {
    const map = productGroup.imageColorMaps[0];
    return {
      ...baseVariant,
      color: map.colorName || baseVariant.color,
      colorCode: map.colorCode || baseVariant.colorCode,
      image: map.imageUrl || baseVariant.image,
    };
  }

  return baseVariant;
};

/**
 * Single source of truth for "is this variant/product actually in stock".
 * Prefers the explicit boolean when present, falls back to stockQuantity
 * otherwise — rather than each call site inventing its own truthiness
 * convention (this codebase previously had four different ones).
 */
export const isInStock = (entity) => {
  if (!entity) return false;
  if (typeof entity.inStock === 'boolean') return entity.inStock;
  return typeof entity.stockQuantity === 'number' && entity.stockQuantity > 0;
};

export const getVariantImages = (variant, apiBaseUrl) => {
  const source = (Array.isArray(variant.variantImages) && variant.variantImages.length > 0)
    ? variant.variantImages
    : (Array.isArray(variant.images) && variant.images.length > 0 ? variant.images : null);

  if (source) {
    return source.map((img) => (img.startsWith('http') ? img : `${apiBaseUrl}${img}`));
  }
  return [variant.image ? (variant.image.startsWith('http') ? variant.image : `${apiBaseUrl}${variant.image}`) : '/images/placeholder.jpg'];
};

/**
 * Build the color-swatch list for a product group, merging every source the
 * admin dashboard can populate instead of picking just one. This used to be
 * a priority chain (image-color-maps, OR colors, OR variants, OR a single
 * color) — meaning if a product had, say, 7 colors tagged via image-color-maps
 * but 12 colors in its `colors` array, only the 7 ever showed up and the
 * other 5 silently vanished from the picker. Sources are merged and
 * de-duplicated by color name (case-insensitive), in priority order —
 * image-color-maps first, since they carry a real photo per color — with
 * later sources only filling in fields an earlier source didn't have (e.g.
 * contributing `inStock` for a color that only had a plain colors-array
 * entry), never overwriting a richer earlier entry.
 */
export const getDisplayColors = (productGroup) => {
  const byName = new Map();

  const addSwatch = (swatch) => {
    if (!swatch.color) return;
    const key = swatch.color.trim().toLowerCase();
    byName.set(key, byName.has(key) ? { ...swatch, ...byName.get(key) } : swatch);
  };

  (productGroup.imageColorMaps || []).forEach((m, idx) => addSwatch({
    id: m.id || `map-${idx}`,
    imageId: m.imageId,
    color: m.colorName,
    colorName: m.colorName,
    colorCode: m.colorCode || '#CCCCCC',
    imageUrl: m.imageUrl,
  }));

  (productGroup.colors || []).forEach((c, idx) => addSwatch({
    id: `color-${idx}`,
    color: c.name || c.color,
    colorName: c.name || c.color,
    colorCode: c.code || c.colorCode || '#CCCCCC',
  }));

  if (productGroup.variants && productGroup.variants.length > 1) {
    const seen = new Set();
    productGroup.variants
      .filter((v) => {
        if (!v.color || seen.has(v.color)) return false;
        seen.add(v.color);
        return true;
      })
      .forEach((v, idx) => addSwatch({
        id: v.id || `variant-${idx}`,
        color: v.color,
        colorName: v.color,
        colorCode: v.colorCode || '#CCCCCC',
        image: v.image,
        inStock: isInStock(v),
      }));
  }

  if (byName.size === 0 && productGroup.color) {
    addSwatch({ id: 'single-color', color: productGroup.color, colorName: productGroup.color, colorCode: productGroup.colorCode || '#CCCCCC' });
  }

  return Array.from(byName.values());
};
